import styled from '@emotion/styled'
import { Image } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { CompanyData } from 'app/containers/Footer/CompanyData'
import { styles, theme } from 'app/theme'
import { useVocabularyData } from 'app/utils/vocabulary'
import { compact, uniqBy } from 'lodash'
import React, { memo } from 'react'

import { PolicyMenuItem, Props as PolicyMenuItemProps } from './PolicyMenuItem'

export interface Props {
  cin?: string
  companyName?: string
  email?: string
  languageCode: string
  phone?: string
  policyMenu?: { [key: string]: PolicyMenuItemProps[] }[]
  rea?: string
  registeredOffice?: string
  shareCapital?: string
  vat?: string
}

export const Bottom = memo(function Bottom({
  cin,
  companyName,
  email,
  languageCode,
  phone,
  policyMenu,
  rea,
  registeredOffice,
  shareCapital,
  vat,
}: Props) {
  return (
    <Container dial={4} row space="between" wrap>
      <LeftSide>
        {companyName && vat ? (
          <CompanyName>
            {companyName} |{' '}
            {`${useVocabularyData('vat', languageCode)}: ${vat}`}
          </CompanyName>
        ) : null}

        <Box row wrap>
          <CompanyData
            className="item-company"
            cin={cin}
            email={email}
            companyName={companyName}
            languageCode={languageCode}
            phone={phone}
            vat={vat}
            rea={rea}
            registeredOffice={registeredOffice}
            shareCapital={shareCapital}
          />

          {policyMenu
            ? uniqBy(policyMenu, 'title').map((item: any, index) => (
                <PolicyMenuItem key={index} {...item} />
              ))
            : null}

          <Credits
            aria-label="QNT Hospitality"
            href="https://www.qnt.it"
            rel="noopenet"
            target="_blank"
          >
            {useVocabularyData('credits', languageCode)}
          </Credits>
        </Box>
      </LeftSide>

      <RightSide dial={4}>
        <Label>{useVocabularyData('certification', languageCode)}</Label>

        <Wrapper>
          <Image src="/certification.png" alt="DCA certification" />
        </Wrapper>
      </RightSide>
    </Container>
  )
})

const Container = styled(FlexBox)`
  position: relative;
  width: 100%;
  padding: 2rem 12.778vw 3.375rem 5.833vw;

  @media (max-width: 1199px) {
    padding: 3rem 0 9rem;
  }
`

const LeftSide = styled.div`
  max-width: 26.25rem;

  @media (max-width: 1199px) {
    max-width: 100%;
    margin-bottom: 3rem;
    padding: 0 3rem;
  }
`

const RightSide = styled(FlexBox)`
  ${styles.label}

  color: ${({ theme }) => theme.colors.variants.neutralLight4};

  @media (max-width: 1199px) {
    width: 100%;
    padding: 3rem 3rem 0;
    border-top: 0.0625rem solid
      ${({ theme }) => theme.colors.variants.primaryLight};
  }
`

const Box = styled(FlexBox)`
  ${styles.label}

  margin-top: 1.25rem;

  > div,
  > a {
    margin-bottom: 0.75rem;
    width: 50%;

    &:nth-last-of-type(even) {
      margin-left: auto;
    }
  }
`

const CompanyName = styled.span`
  color: ${theme.colors.variants.neutralLight4};
  font-family: ${theme.fontFamily.paragraph};
  font-size: 0.875rem;
  line-height: 1.1875rem;
  font-weight: 300;
`

const Policy = styled.div`
  transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);

  &:hover {
    transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);
    opacity: 0.5;
  }
`

const Credits = styled.a`
  transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);

  &:hover {
    transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);
    opacity: 0.5;
  }
`
const Label = styled.p`
  color: ${theme.colors.variants.neutralLight4};
  font-family: ${theme.fontFamily.paragraph};
  font-size: 0.875rem;
  line-height: 1.1875rem;
  font-weight: 300;
  margin-bottom: 1.25rem;
  text-transform: none;
`
const Wrapper = styled.div`
  position: relative;
  width: 5rem;
  aspect-ratio: 1;

  img {
    ${styles.img}
  }
`
